import React, { useMemo } from "react";
import PortalLayout from "@/components/ventrix/PortalLayout";
import { useApiQuery } from "@/hooks/useApi";
import { BookUser } from "lucide-react";

export default function AddressBook() {
  // Draws from historical shipper/consignee entries on the account.
  const { data, isPending } = useApiQuery("address-book", "/v1/shipments?limit=200");
  const items = useMemo(() => {
    if (isPending) return null;              // null = still loading (skeleton)
    const map = new Map();
    (data?.items || []).forEach(s => {
      [["Shipper", s.shipper], ["Consignee", s.consignee]].forEach(([role, p]) => {
        if (!p?.name) return;
        const key = `${role}|${p.name}|${p.city}|${p.country}`;
        if (!map.has(key)) map.set(key, { role, ...p });
      });
    });
    return [...map.values()];
  }, [data, isPending]);

  return (
    <PortalLayout section="customer">
      <div className="vtx-eyebrow">/ Address book</div>
      <h1 className="mt-2 text-3xl font-black text-[#0E2F76] tracking-tighter">Saved shippers & consignees</h1>
      <p className="text-sm text-[#475569] mt-1">Derived from your recent bookings. Adding manual entries lives in v1.1.</p>

      <div className="vtx-card mt-6">
        {items === null && <div className="p-6 space-y-2">{[1,2,3].map(i=><div key={i} className="vtx-skeleton h-14" />)}</div>}
        {items && items.length === 0 && (
          <div className="p-14 text-center">
            <BookUser size={30} className="mx-auto text-[#A9C0E0]" />
            <div className="mt-3 font-bold text-[#0E2F76]">No addresses yet</div>
            <div className="text-sm text-[#475569] mt-1">Book a shipment to start building your address book.</div>
          </div>
        )}
        {items && items.length > 0 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-0">
            {items.map((p, i) => (
              <div key={i} className="p-5 border-r border-b border-[#A9C0E0]/30" data-testid={`addr-${i}`}>
                <div className="vtx-eyebrow">{p.role}</div>
                <div className="mt-2 font-bold text-[#0E2F76]">{p.name}</div>
                {p.company && <div className="text-sm text-[#475569]">{p.company}</div>}
                <div className="text-sm mt-2">{p.address_line1}</div>
                <div className="text-sm">{p.city}, {p.country}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </PortalLayout>
  );
}
